import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FinalCTA from "@/components/FinalCTA";
import { FadeUp } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api } from "@/lib/api";

export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setGuide(null);
    api.get(`/guides/${slug}`).then((r) => setGuide(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <div className="pt-48 pb-32 text-center">
          <p className="font-serif text-4xl text-navy">Guide not found.</p>
          <button data-testid="back-to-guides" onClick={() => navigate("/guides")} className="mt-8 border border-navy px-8 py-3 text-sm font-semibold hover:bg-navy hover:text-cream transition-colors">Browse all guides</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!guide) return <div className="min-h-screen bg-cream"><Navbar /></div>;

  return (
    <div data-testid="guide-article-page">
      <SEO title={guide.seo_title || `${guide.title} | Vitaliah`} description={guide.seo_description || guide.excerpt} />
      <Navbar />
      <article>
        <header className="bg-navy text-cream relative grain">
          <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10 pt-44 pb-20">
            <FadeUp>
              <button data-testid="article-back" onClick={() => navigate("/guides")} className="inline-flex items-center gap-2 text-sm text-cream/60 hover:text-gold transition-colors">
                <ArrowLeft className="h-4 w-4" /> All guides
              </button>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-8 text-xs font-semibold uppercase tracking-[0.25em] text-gold">{guide.category} {guide.read_time ? `— ${guide.read_time}` : ""}</p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1]">{guide.title}</h1>
            </FadeUp>
            <FadeUp delay={0.25}>
              <p className="mt-6 text-lg text-cream/70 leading-relaxed">{guide.excerpt}</p>
            </FadeUp>
          </div>
        </header>
        {guide.featured_image && (
          <div className="mx-auto max-w-4xl px-6 lg:px-10 -mt-0 py-12">
            <FadeUp>
              <div className="overflow-hidden">
                <img src={guide.featured_image} alt={guide.title} className="w-full max-h-[420px] object-cover saturate-[0.75]" />
              </div>
            </FadeUp>
          </div>
        )}
        <div className="mx-auto max-w-3xl px-6 lg:px-10 pb-28" data-testid="article-content">
          {guide.content.split("\n\n").map((p, i) => (
            <FadeUp key={i} delay={0.02 * i}>
              <p className="text-navy/75 leading-[1.9] text-lg mb-8 first:text-xl first:text-navy first:font-serif">{p}</p>
            </FadeUp>
          ))}
        </div>
      </article>
      <FinalCTA />
      <Footer />
    </div>
  );
}
